import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, Clock } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LiveChat from '../components/LiveChat';
import { blogPosts } from '../data/blog';

export default function BlogList() {
  const [featured, ...rest] = blogPosts;

  return (
    <div className="bg-[#050A08] min-h-screen">
      <Header />
      <main className="pt-40 pb-28">
        <section className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl mb-20"
          >
            <span className="inline-block text-emerald-400 font-semibold text-sm uppercase tracking-[0.25em] mb-5">
              / Insights &amp; Ideas
            </span>
            <h1 className="font-display text-5xl md:text-7xl font-black text-white leading-[0.95] mb-6">
              The<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-lime-400">OutsourcedHub</span>{' '}
              Journal
            </h1>
            <p className="text-lg text-slate-400 leading-relaxed">
              Practical frameworks and honest thinking on scaling your business, the smart way.
            </p>
          </motion.div>

          {featured && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mb-20"
            >
              <Link
                to={`/blog/${featured.slug}`}
                className="group block rounded-3xl overflow-hidden border border-white/[0.06] hover:border-emerald-500/40 transition-colors duration-500"
                data-testid={`blog-featured-${featured.slug}`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-5">
                  <div className="lg:col-span-3 relative h-72 lg:h-auto overflow-hidden">
                    <img
                      src={featured.coverImage}
                      alt={featured.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-[#050A08]/60 via-transparent to-transparent" />
                  </div>
                  <div className="lg:col-span-2 bg-[#0D1612] p-8 md:p-12 flex flex-col justify-between">
                    <div>
                      <span className="inline-block text-emerald-400 text-xs uppercase tracking-[0.25em] font-semibold mb-5">
                        {featured.category}
                      </span>
                      <h2 className="font-display text-3xl md:text-4xl font-bold text-white leading-tight mb-4 group-hover:text-emerald-300 transition-colors duration-300">
                        {featured.title}
                      </h2>
                      <p className="text-slate-400 leading-relaxed mb-6">{featured.excerpt}</p>
                    </div>
                    <div className="flex items-center justify-between pt-6 border-t border-white/[0.06]">
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span>{featured.date}</span>
                        <span className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5" />
                          {featured.readTime}
                        </span>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center group-hover:bg-emerald-500 transition-colors duration-300">
                        <ArrowUpRight className="w-4 h-4 text-emerald-400 group-hover:text-[#04120C] transition-colors duration-300" />
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          )}

          {rest.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((post, i) => (
                <motion.div
                  key={post.slug}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 + i * 0.1 }}
                >
                  <Link
                    to={`/blog/${post.slug}`}
                    className="group block h-full rounded-2xl overflow-hidden bg-[#0D1612] border border-white/[0.06] hover:border-emerald-500/40 transition-colors duration-500"
                    data-testid={`blog-card-${post.slug}`}
                  >
                    <div className="h-48 overflow-hidden">
                      <img src={post.coverImage} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="p-6">
                      <span className="inline-block text-emerald-400 text-xs uppercase tracking-[0.2em] font-semibold mb-3">{post.category}</span>
                      <h3 className="font-display text-xl font-bold text-white mb-3 leading-snug group-hover:text-emerald-300 transition-colors duration-300">{post.title}</h3>
                      <p className="text-slate-400 text-sm leading-relaxed line-clamp-3">{post.excerpt}</p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
      <LiveChat />
    </div>
  );
}
